import React, { useEffect, useState, useCallback } from "react";
import {
  fetchUserManagementRolesForApp,
  fetchUserManagementRole,
  fetchUserManagementRoleForApp,
  fetchUserManagementUsers,
  fetchUserManagementPermissions,
  fetchUserManagementPermissionsForApp,
  updateUserManagementRole,
  updateUserManagementRoleForApp,
  deleteUserManagementRole,
  deleteUserManagementRoleForApp,
  createUserManagementRole,
  createUserManagementRoleForApp,
  addUserRole,
  removeUserRole,
  addRolePermission,
  addRolePermissionForApp,
  removeRolePermission,
  removeRolePermissionForApp,
} from "@shared/api/userManagement";
import useListPageUrlState from "../../hooks/useListPageUrlState";
import { ListPageLayout, SortableTh, CtaButton } from "../../components/admin/ListPageLayout";
import Select from "@shared/components/Select";
import { buildAdminPath } from "../../config/routes";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function EditIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function TrashIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function userDisplayName(u) {
  const name = u.name ?? [u.firstname, u.lastname].filter(Boolean).join(" ").trim();
  return name || u.login || `#${u.id}`;
}

const STATUS_PILL = {
  active: "bg-emerald-100 text-emerald-800",
  deleted: "bg-slate-200 text-slate-700",
  blocked: "bg-amber-100 text-amber-800",
};

function StatusPill({ status }) {
  const label = status === "active" ? "Active" : status === "deleted" ? "Deleted" : "Blocked";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_PILL[status] ?? "bg-slate-100 text-slate-700"}`}>
      {label}
    </span>
  );
}

function RoleUserCountPills({ active = 0, deleted = 0, blocked = 0 }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
        active: {active}
      </span>
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700">
        deleted: {deleted}
      </span>
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
        blocked: {blocked}
      </span>
    </div>
  );
}

const USER_SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

function areSetsEqual(left, right) {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
}

function EditRoleModal({ role, customerSlug, appSlug, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const [usersInRole, setUsersInRole] = useState([]);
  const [removedPermissionIds, setRemovedPermissionIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addRemoveError, setAddRemoveError] = useState(null);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [allPermissions, setAllPermissions] = useState([]);
  const [addedPermissionIds, setAddedPermissionIds] = useState(new Set());
  const [addPermissionSearch, setAddPermissionSearch] = useState("");

  useEffect(() => {
    if (!role) return;
    setForm({ name: role.name ?? "", description: role.description ?? "" });
    setUsersInRole(role.users || []);
    setRemovedPermissionIds(new Set());
    setAddedPermissionIds(new Set());
    setUserSearch("");
    setSearchResults([]);
    setPermissionSearch("");
    setAddPermissionSearch("");
  }, [role]);

  useEffect(() => {
    if (!customerSlug || !role) return;
    const fetchPerms = appSlug
      ? () => fetchUserManagementPermissionsForApp(customerSlug, appSlug)
      : () => fetchUserManagementPermissions(customerSlug);
    fetchPerms()
      .then((res) => setAllPermissions(Array.isArray(res) ? res : []))
      .catch(() => setAllPermissions([]));
  }, [customerSlug, appSlug, role]);

  const handleAddPermission = useCallback(
    async (permissionId) => {
      setAddRemoveError(null);
      try {
        if (appSlug) {
          await addRolePermissionForApp(customerSlug, appSlug, role.id, permissionId);
        } else {
          await addRolePermission(customerSlug, role.id, permissionId);
        }
        setAddedPermissionIds((prev) => new Set(prev).add(String(permissionId)));
      } catch (err) {
        setAddRemoveError(err.message || "Failed to add permission");
      }
    },
    [customerSlug, appSlug, role.id]
  );

  useEffect(() => {
    if (!customerSlug || !role || userSearch.trim().length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(() => {
      setSearchLoading(true);
      setAddRemoveError(null);
      fetchUserManagementUsers(customerSlug, { query: userSearch.trim(), per_page: 20 })
        .then((res) => setSearchResults(res.data || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [customerSlug, role, userSearch]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const userIdsInRole = useCallback(() => new Set(usersInRole.map((u) => String(u.id))), [usersInRole]);

  const handleAddUser = useCallback(
    async (user) => {
      setAddRemoveError(null);
      try {
        await addUserRole(customerSlug, user.id, role.id);
        setUsersInRole((prev) => [...prev, { id: user.id, login: user.login, name: (user.name ?? [user.firstname, user.lastname].filter(Boolean).join(" ").trim()) || null, status: "active" }]);
      } catch (err) {
        setAddRemoveError(err.message || "Failed to add user");
      }
    },
    [customerSlug, role.id]
  );

  const handleRemoveUser = useCallback(
    async (user) => {
      setAddRemoveError(null);
      try {
        await removeUserRole(customerSlug, user.id, role.id);
        setUsersInRole((prev) => prev.filter((u) => String(u.id) !== String(user.id)));
      } catch (err) {
        setAddRemoveError(err.message || "Failed to remove user");
      }
    },
    [customerSlug, role.id]
  );

  const removePermission = useCallback((permissionId) => {
    setRemovedPermissionIds((prev) => new Set(prev).add(String(permissionId)));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!role || !customerSlug) return;
      setSaving(true);
      setError(null);
      try {
        if (appSlug) {
          await updateUserManagementRoleForApp(customerSlug, appSlug, role.id, {
            name: form.name || null,
            description: form.description || null,
          });
          for (const pid of removedPermissionIds) {
            await removeRolePermissionForApp(customerSlug, appSlug, role.id, pid);
          }
        } else {
          await updateUserManagementRole(customerSlug, role.id, {
            name: form.name || null,
            description: form.description || null,
          });
          for (const pid of removedPermissionIds) {
            await removeRolePermission(customerSlug, role.id, pid);
          }
        }
        onSaved({ type: "success", message: "Role updated." });
        onClose();
      } catch (err) {
        setError(err.message || "Failed to save");
        onSaved({ type: "error", message: err.message || "Failed to save" });
      } finally {
        setSaving(false);
      }
    },
    [role, customerSlug, appSlug, form, removedPermissionIds, onSaved, onClose]
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [onClose]);

  if (!role) return null;

  const initialUserIds = new Set((role.users || []).map((u) => String(u.id)));
  const currentUserIds = new Set(usersInRole.map((u) => String(u.id)));
  const usersChanged = !areSetsEqual(initialUserIds, currentUserIds);
  const originalPermissionIds = new Set((role.permissions || []).map((p) => String(p.id)));
  const currentPermissionIds = new Set(originalPermissionIds);
  removedPermissionIds.forEach((id) => currentPermissionIds.delete(String(id)));
  addedPermissionIds.forEach((id) => currentPermissionIds.add(String(id)));
  const permissionsChanged = !areSetsEqual(originalPermissionIds, currentPermissionIds);
  const roleNameChanged = form.name !== (role.name ?? "");
  const roleDescriptionChanged = form.description !== (role.description ?? "");
  const isDirty = roleNameChanged || roleDescriptionChanged || usersChanged || permissionsChanged;

  const currentRolePermissionIds = new Set(
    [...(role.permissions || []).map((p) => String(p.id)), ...addedPermissionIds].filter(
      (id) => !removedPermissionIds.has(String(id))
    )
  );
  const visiblePermissions = (role.permissions || [])
    .filter((p) => !removedPermissionIds.has(String(p.id)))
    .concat(allPermissions.filter((p) => addedPermissionIds.has(String(p.id))));
  const permissionsNotInRole = allPermissions.filter((p) => !currentRolePermissionIds.has(String(p.id)));

  const permissionSearchLower = (permissionSearch || "").trim().toLowerCase();
  const filteredVisiblePermissions =
    permissionSearchLower === ""
      ? visiblePermissions
      : visiblePermissions.filter(
          (p) =>
            (p.name || "").toLowerCase().includes(permissionSearchLower) ||
            (p.description || "").toLowerCase().includes(permissionSearchLower)
        );
  const addPermissionSearchLower = (addPermissionSearch || "").trim().toLowerCase();
  const filteredPermissionsNotInRole =
    addPermissionSearchLower === ""
      ? permissionsNotInRole
      : permissionsNotInRole.filter(
          (p) =>
            (p.name || "").toLowerCase().includes(addPermissionSearchLower) ||
            (p.description || "").toLowerCase().includes(addPermissionSearchLower)
        );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-role-title"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <h2 id="edit-role-title" className="text-lg font-semibold text-slate-800">
            Edit role
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 animate-notice-slide-in" role="alert">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="edit-role-name" className="block text-sm font-medium text-slate-700 mb-1">
              Name
            </label>
            <input
              id="edit-role-name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label htmlFor="edit-role-description" className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              id="edit-role-description"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-slate-700 mb-2">Users in this role</span>
            {addRemoveError && (
              <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-800 animate-notice-slide-in" role="alert">
                {addRemoveError}
              </div>
            )}
            {role.active_users_count != null && (
              <div className="mb-3">
                <RoleUserCountPills
                  active={role.active_users_count ?? 0}
                  deleted={role.deleted_users_count ?? 0}
                  blocked={role.blocked_users_count ?? 0}
                />
              </div>
            )}
            {usersInRole.length === 0 ? (
              <p className="text-sm text-slate-500 mb-3">No users assigned.</p>
            ) : (
              <ul className="space-y-1.5 mb-4 max-h-28 overflow-y-auto">
                {usersInRole.map((u) => (
                  <li key={u.id} className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-slate-50">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-slate-700 truncate">{userDisplayName(u)}</span>
                      {u.status && <StatusPill status={u.status} />}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(u)}
                      className="p-1 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-slate-500 shrink-0"
                      title="Remove from role"
                      aria-label={`Remove ${userDisplayName(u)} from role`}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <span className="block text-sm font-medium text-slate-700 mb-1">Add user to role</span>
            <p className="text-xs text-slate-500 mb-2">Search by login, email, or first/last name (min 2 characters).</p>
            <input
              type="search"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users…"
              className="w-full px-3 py-2 mb-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              aria-label="Search users by login, email, or name"
            />
            {searchLoading && <p className="text-xs text-slate-500 mb-1">Searching…</p>}
            {userSearch.trim().length >= MIN_SEARCH_LENGTH && !searchLoading && (
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {searchResults.length === 0 ? (
                  <li className="text-sm text-slate-500 py-1">No users found.</li>
                ) : (
                  searchResults.map((user) => {
                    const inRole = userIdsInRole().has(String(user.id));
                    return (
                      <li key={user.id} className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-slate-50">
                        <span className="text-sm text-slate-700 truncate">{userDisplayName(user)}</span>
                        <button
                          type="button"
                          onClick={() => handleAddUser(user)}
                          disabled={inRole}
                          className="text-xs font-medium text-slate-600 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-slate-500 rounded px-2 py-1 border border-slate-300 hover:bg-slate-100"
                        >
                          {inRole ? "In role" : "Add"}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>
          <div>
            <span className="block text-sm font-medium text-slate-700 mb-2">Permissions</span>
            <p className="text-xs text-slate-500 mb-2">Remove a permission by clicking the × next to it.</p>
            {visiblePermissions.length > 0 && (
              <input
                type="search"
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                placeholder="Search by name or description…"
                className="w-full px-3 py-2 mb-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                aria-label="Search permissions"
              />
            )}
            {visiblePermissions.length === 0 ? (
              <p className="text-sm text-slate-500">No permissions assigned.</p>
            ) : filteredVisiblePermissions.length === 0 ? (
              <p className="text-sm text-slate-500">No permissions match the search.</p>
            ) : (
              <ul className="space-y-1.5 max-h-32 overflow-y-auto">
                {filteredVisiblePermissions.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-slate-50">
                    <span className="text-sm text-slate-700">{p.name}</span>
                        <button
                          type="button"
                          onClick={() => removePermission(p.id)}
                          className="p-1 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-slate-500"
                          title="Remove permission"
                          aria-label={`Remove ${p.name}`}
                        >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <span className="block text-sm font-medium text-slate-700 mb-1 mt-4">Add permission</span>
            {permissionsNotInRole.length > 0 && (
              <input
                type="search"
                value={addPermissionSearch}
                onChange={(e) => setAddPermissionSearch(e.target.value)}
                placeholder="Search by name or description…"
                className="w-full px-3 py-2 mb-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                aria-label="Search permissions to add"
              />
            )}
            {permissionsNotInRole.length === 0 ? (
              <p className="text-sm text-slate-500">All permissions are already in this role.</p>
            ) : filteredPermissionsNotInRole.length === 0 ? (
              <p className="text-sm text-slate-500">No permissions match the search.</p>
            ) : (
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {filteredPermissionsNotInRole.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-slate-50">
                    <span className="text-sm text-slate-700">{p.name}</span>
                    <button
                      type="button"
                      onClick={() => handleAddPermission(p.id)}
                      className="text-xs font-medium text-slate-600 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 rounded px-2 py-1 border border-slate-300 hover:bg-slate-100"
                    >
                      Add
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !isDirty}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-lg hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddRoleModal({ customerSlug, appSlug, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isDirty = Boolean(form.name.trim() || form.description.trim());

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!customerSlug) return;
      setSaving(true);
      setError(null);
      try {
        if (appSlug) {
          await createUserManagementRoleForApp(customerSlug, appSlug, {
            name: form.name || null,
            description: form.description || null,
          });
        } else {
          await createUserManagementRole(customerSlug, {
            name: form.name || null,
            description: form.description || null,
          });
        }
        onSaved({ type: "success", message: "Role created." });
        onClose();
      } catch (err) {
        setError(err.message || "Failed to create role");
        onSaved({ type: "error", message: err.message || "Failed to create role" });
      } finally {
        setSaving(false);
      }
    },
    [customerSlug, appSlug, form, onSaved, onClose]
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-role-title"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200">
          <h2 id="add-role-title" className="text-lg font-semibold text-slate-800">
            Add Role
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 animate-notice-slide-in" role="alert">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="add-role-name" className="block text-sm font-medium text-slate-700 mb-1">
              Name
            </label>
            <input
              id="add-role-name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label htmlFor="add-role-description" className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              id="add-role-description"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Cancel
            </button>
            <CtaButton type="submit" disabled={saving || !isDirty}>
              {saving ? "Creating…" : "Add role"}
            </CtaButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminRolesPage({
  customerSlug,
  appSlug = null,
  selectedApp: _selectedApp = null,
  apps = [],
  pathname = "",
  onNavigate,
}) {
  const { fetchParams, query, setQuery, page, setPage, perPage, setPerPage, sort, order, setSort } = useListPageUrlState();
  const [result, setResult] = useState({ data: [], meta: { total_count: 0, total_pages: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [showAddRole, setShowAddRole] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  const loadList = useCallback(() => {
    if (!customerSlug || !appSlug) return;
    setLoading(true);
    setError(null);
    fetchUserManagementRolesForApp(customerSlug, appSlug, fetchParams)
      .then(setResult)
      .catch((err) => setError(err.message || "Failed to load roles"))
      .finally(() => setLoading(false));
  }, [customerSlug, appSlug, fetchParams]);

  useEffect(() => {
    if (!customerSlug || !appSlug) return;
    loadList();
  }, [customerSlug, appSlug, loadList]);

  const openEdit = useCallback(
    async (role) => {
      setEditRole(role);
      setModalLoading(true);
      try {
        const fullRole = appSlug
          ? await fetchUserManagementRoleForApp(customerSlug, appSlug, role.id)
          : await fetchUserManagementRole(customerSlug, role.id);
        setEditRole(fullRole);
      } catch (err) {
        setError(err.message || "Failed to load role");
        setEditRole(null);
      } finally {
        setModalLoading(false);
      }
    },
    [customerSlug, appSlug]
  );

  const handleDelete = useCallback(
    async (role) => {
      if (!window.confirm(`Delete role "${role.name}"? This will remove the role from all users.`)) return;
      try {
        if (appSlug) {
          await deleteUserManagementRoleForApp(customerSlug, appSlug, role.id);
        } else {
          await deleteUserManagementRole(customerSlug, role.id);
        }
        setNotice({ type: "success", message: "Role deleted." });
        loadList();
      } catch (err) {
        setError(err.message || "Failed to delete role");
        setNotice({ type: "error", message: err.message || "Failed to delete role" });
      }
    },
    [customerSlug, appSlug, loadList]
  );

  const totalCount = result.meta?.total_count ?? result.data.length;
  const totalPages = result.meta?.total_pages ?? Math.max(1, Math.ceil(totalCount / perPage));
  const pageData = result.meta ? result.data : result.data.slice((page - 1) * perPage, page * perPage);

  const appOptions = (apps || []).map((a) => ({
    value: a.slug ?? a.appSlug ?? a.id,
    label: a.displayName || a.name || a.slug || a.id || "—",
  }));
  const handleAppChange = (slug) => {
    if (onNavigate && slug) onNavigate(buildAdminPath(pathname, "roles", slug));
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <h2 className="text-xl font-semibold text-slate-800 shrink-0">Roles</h2>
        <div className="w-full sm:w-64">
          <Select
            id="roles-app-select"
            value={appSlug ?? undefined}
            onChange={handleAppChange}
            options={appOptions}
            placeholder="Select App"
          />
        </div>
      </div>
      {!appSlug && (
        <p className="text-slate-600 text-sm">Select an app to view and manage roles.</p>
      )}
      {appSlug && (
      <ListPageLayout
        title=""
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search by name or description…"
        actions={<CtaButton onClick={() => setShowAddRole(true)}>Add Role</CtaButton>}
        notice={notice}
        onNoticeDismiss={() => setNotice(null)}
        loading={loading}
        error={error}
        totalCount={totalCount}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
      >
        <table className="min-w-full divide-y divide-slate-200" role="grid">
          <thead className="bg-slate-100 border-b border-slate-200">
            <tr>
              <SortableTh
                label="Name"
                sortKey="name"
                currentSort={sort}
                currentOrder={order}
                onSort={setSort}
                className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
              />
              <SortableTh
                label="Description"
                sortKey="description"
                currentSort={sort}
                currentOrder={order}
                onSort={setSort}
                className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
              />
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                Users
              </th>
              <SortableTh
                label="Updated at"
                sortKey="updated_at"
                currentSort={sort}
                currentOrder={order}
                onSort={setSort}
                className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
              />
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No roles found.
                </td>
              </tr>
            ) : (
              pageData.map((role) => (
                <tr key={role.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-slate-900">{role.name ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{role.description ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    <RoleUserCountPills
                      active={role.active_users_count ?? 0}
                      deleted={role.deleted_users_count ?? 0}
                      blocked={role.blocked_users_count ?? 0}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{formatDate(role.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(role)}
                        className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                        title="Edit"
                        aria-label="Edit role"
                      >
                        <EditIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(role)}
                        className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        title="Delete"
                        aria-label="Delete role"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ListPageLayout>
      )}

      {showAddRole && (
        <AddRoleModal
          customerSlug={customerSlug}
          appSlug={appSlug}
          onClose={() => setShowAddRole(false)}
          onSaved={(result) => {
            if (result?.message) setNotice({ type: result.type || "success", message: result.message });
            loadList();
          }}
        />
      )}
      {editRole &&
        (modalLoading ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" aria-busy="true">
            <div className="bg-white rounded-xl shadow-xl px-8 py-6 text-slate-600">Loading…</div>
          </div>
        ) : (
          <EditRoleModal
            role={editRole}
            customerSlug={customerSlug}
            appSlug={appSlug}
            onClose={() => setEditRole(null)}
            onSaved={(result) => {
              if (result?.message) setNotice({ type: result.type || "success", message: result.message });
              loadList();
            }}
          />
        ))}
    </>
  );
}
